import { WebsiteGameConnection } from "./WebsiteConnection";
import type { ConnectionOptions } from "./wsConfig";
import { sharedWsKey, WS_POOL_RELEASE_MS } from "./wsConfig";

type SharedEntry = { conn: WebsiteGameConnection; refs: number; timer: number | null };

const sharedPool = new Map<string, SharedEntry>();
const inflightSharedConnects = new Map<string, Promise<void>>();

export function acquireSharedConnection(opts: ConnectionOptions): { key: string; conn: WebsiteGameConnection } {
  const gid = (opts.guildId ?? "").trim();
  const tok = opts.token ?? "";
  const key = sharedWsKey(gid, tok);
  const existing = sharedPool.get(key);
  if (existing) {
    if (existing.timer !== null) {
      window.clearTimeout(existing.timer);
      existing.timer = null;
    }
    existing.refs += 1;
    return { key, conn: existing.conn };
  }
  const conn = new WebsiteGameConnection(opts);
  sharedPool.set(key, { conn, refs: 1, timer: null });
  return { key, conn };
}

export function releaseSharedConnection(key: string): void {
  const entry = sharedPool.get(key);
  if (!entry) return;
  entry.refs = Math.max(0, entry.refs - 1);
  if (entry.refs > 0) return;
  if (entry.timer !== null) window.clearTimeout(entry.timer);
  entry.timer = window.setTimeout(() => {
    const cur = sharedPool.get(key);
    if (!cur) return;
    if (cur.refs > 0) {
      cur.timer = null;
      return;
    }
    sharedPool.delete(key);
    inflightSharedConnects.delete(key);
    try {
      cur.conn.disconnect();
    } catch {
      return;
    }
  }, WS_POOL_RELEASE_MS);
}

export function dedupedSharedConnect(key: string, conn: WebsiteGameConnection): Promise<void> {
  if (conn.isLive()) return Promise.resolve();
  const pending = inflightSharedConnects.get(key);
  if (pending) return pending;
  const task = conn.connect();
  inflightSharedConnects.set(key, task);
  const clear = () => {
    if (inflightSharedConnects.get(key) === task) inflightSharedConnects.delete(key);
  };
  task.then(clear, clear);
  return task;
}
